import { z } from 'zod';

import { Box, Form } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useProductAccess } from '@common/hooks/useProductAccess';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';
import { useUpdateAiBdcSettings } from '../api/useUpdateAiBdcSettings';

const preQualSchema = z.object({
	enabled: z.boolean().optional(),
});

export function PreQualificationSection() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const { hasAccess: hasTransactionLayer, isLoading: isTLLoading } = useProductAccess('transactionLayer');
	const updateMutation = useUpdateAiBdcSettings(spId);

	const handleSubmit = async (
		data: z.infer<typeof preQualSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				skills: {
					...settings?.skills,
					prequalification: { enabled: data.enabled },
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	if (isTLLoading) return null;

	if (!hasTransactionLayer) {
		return (
			<Box padding='24' background='secondary' rounding='12'>
				<div data-settings-disabled>
					<Form
						name='ai-bdc-pre-qualification'
						schema={preQualSchema}
						defaultValues={{ enabled: false }}
						onSubmit={async () => ({ isSuccess: false })}
						title='Pre-qualification'
						titleLevel='subgroup'
						titleWeight='semibold'
						titleMaxWidth='full'
						description='Allow the AI assistant to collect buyer pre-qualification information during conversations — such as budget, timeline, trade-in details, and credit situation.'
						tag={{ children: 'Coming soon', size: 'small' }}
						toggle={{ fieldName: 'enabled' }}>
						{null}
					</Form>
				</div>
			</Box>
		);
	}

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-pre-qualification'
				schema={preQualSchema}
				defaultValues={{
					enabled: settings?.skills?.prequalification?.enabled ?? false,
				}}
				onSubmit={handleSubmit}
				title='Pre-qualification'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Allow the AI assistant to collect buyer pre-qualification information during conversations — such as budget, timeline, trade-in details, and credit situation.'
				tag={{ children: 'Recommended', accent: 1, appearance: 'solid', size: 'small' }}
				toggle={{ fieldName: 'enabled' }}
				showSaveAndClear>
				{null}
			</Form>
		</Box>
	);
}
